import React from 'react';
import dialog from 'dialog';
import { getGlobalState } from 'state';
import FloatingInput from 'elements/floating-input';
import WalkupSong from 'components/walkup-song';
import Card from 'elements/card';
import ListButton from 'elements/list-button';
import { goBack, goHome } from 'actions/route';
import network from 'network';
import FloatingReactAsyncCreatableSelect from 'elements/floating-react-async-creatable-select';
import IconButton from 'elements/icon-button';
import type { Player } from 'shared-lib';

interface YouTubeOption {
  label: string;
  value: string | undefined;
  thumbnail: string;
}

interface CardPlayerEditProps {
  player: Player;
  isNew: boolean;
}

interface CardPlayerEditState {
  playerGender: string;
  playerName: string;
  playerSongLink: string;
  playerSongStart: string | number;
  youTubeKey: number;
}

export default class CardPlayerEdit extends React.Component<
  CardPlayerEditProps,
  CardPlayerEditState
> {
  isNew: boolean;
  isPristine: boolean;
  queryYouTube: (query: string) => Promise<YouTubeOption[]>;
  formatOptionLabel: (
    option: YouTubeOption,
    context: { context: string }
  ) => JSX.Element;
  onYouTubeSelect: () => (query: string) => Promise<YouTubeOption[]>;
  homeOrBack: (type: string) => (cb?: () => void) => boolean | void;
  handleConfirmClick: () => void;
  handleCancelClick: () => void;
  handleDeleteClick: () => void;
  handlePlayerNameChange: (value: string) => void;
  handleGenderChange: (ev: React.ChangeEvent<HTMLInputElement>) => void;
  handleYouTubeSelection: (selectedOption: YouTubeOption) => void;
  isValidYouTubeVideoLink: (url: string) => boolean;
  handleSongStartChange: () => void;
  handleSongHelpClick: () => void;

  constructor(props: CardPlayerEditProps) {
    super(props);

    this.isNew = props.isNew;

    this.state = {
      playerGender: props.player.gender,
      playerName: props.player.name,
      playerSongLink: props.player.songLink ?? '',
      playerSongStart: props.player.songStart ?? '',
      youTubeKey: 0, // Used as a react 'key' so we can reset the floating label when an a youtube search changes the field
    };

    const buildPlayer = () => {
      const player = JSON.parse(JSON.stringify(props.player));
      player.name = this.state.playerName;
      player.gender = this.state.playerGender;
      player.songLink = this.state.playerSongLink;
      player.songStart = parseInt(String(this.state.playerSongStart || 0));
      return player;
    };

    this.isPristine = props.isNew ? false : true;

    this.queryYouTube = async function (query) {
      // Do the query
      const searchTerms = encodeURIComponent(query);
      const url = `server/youtube?q=${searchTerms}`;
      let response = await network.request('GET', url);

      const listOfVideos = (response.body as any).items;

      // Filter so we just get what we want
      let results: YouTubeOption[] = [];
      for (let i = 0; i < listOfVideos.length; i++) {
        results.push({
          label: listOfVideos[i].snippet.title,
          value: listOfVideos[i].id.videoId,
          thumbnail: listOfVideos[i].snippet.thumbnails.default.url,
        });
      }
      console.log('YoutubeResults', results);
      return results;
    };

    this.formatOptionLabel = ({ value, label, thumbnail }, { context }) => {
      if (context === 'value') {
        return <div>{label}</div>;
      } else {
        // 'menu'
        return (
          <div style={{ display: 'flex', position: 'relative' }}>
            <div>
              <img src={thumbnail} alt={''} width="120" height="90"></img>
            </div>
            <div
              style={{
                position: 'absolute',
                top: '50%',
                msTransform: 'translateY(-50%)',
                transform: 'translateY(-50%)',
                wordWrap: 'break-word',
                marginLeft: '125px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                color: 'black',
                height: '87px',
              }}
            >
              {label}
            </div>
          </div>
        );
      }
    };

    this.onYouTubeSelect = (function () {
      let timeout: ReturnType<typeof setTimeout> | null = null;
      return async (query: string): Promise<YouTubeOption[]> => {
        if (query.length === 0) {
          return [];
        }

        // Don't call the API if the query is less than 4 characters
        if (query.length <= 3) {
          return [
            {
              label: 'Enter more than 3 characters to search',
              value: undefined,
              thumbnail: '/assets/alert.svg',
            },
          ];
        }

        // Don't call the API if the user entered a valid YouTube link
        if (this.isValidYouTubeVideoLink(query)) {
          return [{ label: 'Link: ' + query, value: query, thumbnail: '/assets/link.svg' }];
        }

        // Debounced Query
        return new Promise((resolve) => {
          if (timeout) clearTimeout(timeout);
          timeout = setTimeout(async () => {
            timeout = null;
            const result = await this.queryYouTube(query);
            resolve(result);
          }, 2000);
        });
      };
    }).call(this);

    this.homeOrBack = (type) => (cb) => {
      if (!this.isPristine) {
        dialog.show_confirm(
          props.isNew
            ? 'Are you sure you wish to discard this player?'
            : 'Are you sure you wish to discard changes to this player?',
          () => {
            if (props.isNew) {
              getGlobalState().removePlayer(props.player.id);
            }
            if (type === 'home') {
              goHome();
            } else {
              goBack();
            }
          }
        );
        return true;
      }
      if (cb) {
        cb();
      }
    };

    this.handleConfirmClick = function () {
      getGlobalState().replacePlayer(props.player.id, buildPlayer());
      goBack();
    };

    this.handleCancelClick = () => {
      this.homeOrBack('back')(goBack);
    };

    this.handleDeleteClick = function () {
      dialog.show_confirm(
        'Are you sure you want to delete the player "' +
          this.state.playerName +
          '"?',
        () => {
          if (getGlobalState().removePlayer(props.player.id)) {
            goBack();
          } else {
            dialog.show_notification(
              `Unable to delete player ${this.state.playerName}. This player either has existing plate appearances and/or is listed on at least one game's lineup.`
            );
          }
        }
      );
    }.bind(this);

    this.handlePlayerNameChange = (value) => {
      this.isPristine = false;
      this.setState({
        playerName: value,
      });
    };

    this.handleGenderChange = function (ev) {
      this.isPristine = false;
      this.setState({
        playerGender: ev.target.value,
      });
    }.bind(this);

    this.handleYouTubeSelection = function (selectedOption) {
      this.isPristine = false;
      let newValue = selectedOption.value;
      if (this.isValidYouTubeVideoLink(newValue)) {
        // This is a link, extract the video id
        let dataArray = newValue.split(/\/|\?|=|%3D|&/);
        for (let i = 0; i < dataArray.length; i++) {
          let results = dataArray[i].match(/[0-9a-zA-Z-_]{11}/);
          if (results && results.length === 1) {
            this.setState({
              playerSongLink: results[0],
              youTubeKey: this.state.youTubeKey + 1, // force select to update
            });
          }
        }
      } else {
        // Not a link, search populated a video id, set state directly
        this.setState({
          playerSongLink: newValue,
          youTubeKey: this.state.youTubeKey + 1, // force select to update
        });
      }
    }.bind(this);

    this.isValidYouTubeVideoLink = function (url) {
      let result = url.match(
        /(?:https?:\/\/)?(?:youtu\.be\/|(?:www\.|m\.)?youtube\.com\/(?:watch|v|embed)(?:\.php)?(?:\?.*v=|\/))([a-zA-Z0-9_-]{11})/
      );
      return result == null ? false : true;
    };

    this.handleSongStartChange = function () {
      this.isPristine = false;
      this.setState({
        playerSongStart: (document.getElementById('songStart') as HTMLInputElement | null)?.value ?? '',
      });
    }.bind(this);

    this.handleSongHelpClick = function () {
      dialog.show_notification(
        <div>
          <b>Walkup Song</b>
          <div style={{ margin: '1rem' }}>
            Clips can be played from the player&apos;s plate appearance page.
          </div>
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <img
              alt="Plate appearance scoring screenshot"
              src="/assets/help-walkup.svg"
            ></img>
          </div>
        </div>,
        undefined
      );
    };
  }

  componentDidMount() {
    // TODO: can we use defaultChecked instead?
    const elem = (() => {
      if (this.state.playerGender === 'F') {
        return document.getElementById('femaleGenderChoice');
      } else {
        return document.getElementById('maleGenderChoice');
      }
    })();
    if (elem) {
      (elem as HTMLInputElement).checked = true;
    }
  }

  renderPlayerEdit() {
    return (
      <>
        <div className="auth-input-container">
          <FloatingInput
            inputId="playerName"
            label="Player Name"
            onChange={this.handlePlayerNameChange}
            defaultValue={this.state.playerName}
          />
          <div className="radio-button">
            <fieldset className="group">
              <legend className="group-legend">Gender</legend>
              <div className="radio-button-option">
                <input
                  type="radio"
                  name="gender"
                  value="M"
                  id="maleGenderChoice"
                  onChange={this.handleGenderChange}
                />
                <label htmlFor="maleGenderChoice">Male</label>
              </div>
              <div className="radio-button-option">
                <input
                  type="radio"
                  name="gender"
                  value="F"
                  id="femaleGenderChoice"
                  onChange={this.handleGenderChange}
                />
                <label htmlFor="femaleGenderChoice">Female</label>
              </div>
            </fieldset>
          </div>
          <fieldset style={{ padding: '5px' }} className="group">
            <legend className="group-legend">Walk Up Song (Optional)</legend>
            <FloatingReactAsyncCreatableSelect
              defaultValue={{
                label: this.state.playerSongLink
                  ? `https://youtu.be/${this.state.playerSongLink}`
                  : '',
                value: this.state.playerSongLink,
              }}
              label={'Search YouTube / Paste YouTube Link'}
              loadOptions={this.onYouTubeSelect as any}
              formatOptionLabel={this.formatOptionLabel as any}
              onChange={this.handleYouTubeSelection as any}
              isValidNewOption={() => {
                return false;
              }}
              key={this.state.youTubeKey}
            />
            <FloatingInput
              inputId="songStart"
              label="Start time in seconds"
              type="number"
              min={0}
              step={1}
              maxLength={50}
              onChange={this.handleSongStartChange}
              defaultValue={String(this.state.playerSongStart || '')}
            />
            <div id="songLabel" className="song-label">
              Preview
            </div>
            <div id="songWrapper" className="song-preview-container">
              <WalkupSong
                songLink={this.state.playerSongLink}
                songStart={Number(this.state.playerSongStart || 0)}
                width={128}
                height={128}
              ></WalkupSong>
              <IconButton
                alt="help"
                className="help-icon"
                src="/assets/help.svg"
                onClick={this.handleSongHelpClick}
                invert
              />
            </div>
          </fieldset>
        </div>
        {this.renderSaveOptions()}
      </>
    );
  }

  renderSaveOptions() {
    return (
      <>
        <>
          <ListButton
            id="save"
            type="primary-button"
            onClick={this.handleConfirmClick}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <IconButton src="/assets/check.svg" alt="save" />
              <span>Save</span>
            </div>
          </ListButton>
          <ListButton
            id="cancel"
            type="edit-button"
            onClick={this.handleCancelClick}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <IconButton src="/assets/cancel.svg" alt="cancel" invert />
              <span>Cancel</span>
            </div>
          </ListButton>
          {this.props.isNew ? null : (
            <ListButton
              id="delete"
              type="delete-button"
              onClick={this.handleDeleteClick}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <IconButton src="/assets/delete.svg" alt="delete" />
                <span>Delete</span>
              </div>
            </ListButton>
          )}
        </>
      </>
    );
  }

  render() {
    return (
      <Card
        title="Edit Player"
        leftHeaderProps={{ onClick: this.homeOrBack('back') }}
        rightHeaderProps={{ onClick: this.homeOrBack('home') }}
      >
        {this.renderPlayerEdit()}
      </Card>
    );
  }
}
